import { ActionButton, TextArea, TextField } from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import Close from '@react-spectrum/s2/icons/Close';
import type { ReactNode } from 'react';

import type { useEditableTagList } from '@/hooks/useEditableTagList';

type EditableTagListProps = {
	tags: ReturnType<typeof useEditableTagList>;
	inputComponent?: 'TextField' | 'TextArea';
	placeholder?: string;
	renderEditRow?: (opts: {
		tag: string;
		index: number;
		onUpdate: (index: number, value: string) => void;
		onRemove: (index: number) => void;
	}) => ReactNode;
	renderAddRow?: (opts: {
		value: string;
		onChange: (value: string) => void;
		onAdd: () => void;
	}) => ReactNode;
};

export const EditableTagList = ({
	tags,
	inputComponent = 'TextField',
	placeholder = 'Add new',
	renderEditRow,
	renderAddRow,
}: EditableTagListProps) => {
	const Input = inputComponent === 'TextArea' ? TextArea : TextField;

	return (
		<div className="flex flex-col gap-3">
			{tags.draftTags.map((tag, i) =>
				renderEditRow ? (
					renderEditRow({
						tag,
						index: i,
						onUpdate: tags.onUpdateTag,
						onRemove: tags.onRemoveTag,
					})
				) : (
					<div key={i} className="flex w-full items-center gap-1">
						<div className="flex-1">
							<Input
								aria-label={placeholder}
								value={tag}
								onChange={(val) => tags.onUpdateTag(i, val)}
							/>
						</div>
						<ActionButton
							aria-label={`Remove ${tag}`}
							isQuiet
							size="XS"
							onPress={() => tags.onRemoveTag(i)}
						>
							<Close />
						</ActionButton>
					</div>
				),
			)}
			{renderAddRow ? (
				renderAddRow({
					value: tags.newValue,
					onChange: tags.setNewValue,
					onAdd: tags.onAddTag,
				})
			) : (
				<div className="flex w-full items-center gap-1">
					<div className="flex-1">
						<Input
							aria-label={`New ${placeholder.toLowerCase()}`}
							value={tags.newValue}
							onChange={tags.setNewValue}
							placeholder={placeholder}
						/>
					</div>
					<ActionButton
						aria-label={`Add ${placeholder.toLowerCase()}`}
						isQuiet
						size="XS"
						onPress={tags.onAddTag}
					>
						<Add />
					</ActionButton>
				</div>
			)}
		</div>
	);
};
